import type { VisibilityLevel } from './audit';
import type { TargetZone, TargetCategory } from './targets';
import type { AllowanceFigures, SaveWarnings, FirstRunNotice, PromptDispatchMode } from './brand-allowances';

/**
 * Brand Perception is split into FOUR axes, each with its own scoring rule.
 * `comparison` (« Comparaison ») is new (Prompt Studio 2026-10-15): a prompt that
 * compares the subject with an alternative is scored on who wins per criterion.
 *
 * See docs/features/brand-perception.md and 00-contracts.md C1.
 */
export type BrandQueryAxis = 'narrative' | 'authority' | 'referral' | 'comparison';

/** Pending axis — used before classification succeeds (C2). */
export const BRAND_QUERY_PENDING_AXIS = 'pending' as const;

/** The five intent types a prompt can have (C4). */
export type BrandQueryIntent = 'learn' | 'recommend' | 'compare' | 'describe' | 'reputation';

/**
 * Which intents belong to which axis.  Every intent belongs to exactly ONE axis.
 * `compare` → `comparison` (C1).
 */
export const BRAND_QUERY_INTENTS: Record<BrandQueryAxis, readonly BrandQueryIntent[]> = {
  authority:  ['learn'],
  narrative:  ['describe', 'reputation'],
  referral:   ['recommend'],
  comparison: ['compare'],
} as const;

/**
 * Reverse map: intent → axis.  Derived from BRAND_QUERY_INTENTS, never hand-written.
 */
export const AXIS_BY_INTENT: Record<BrandQueryIntent, BrandQueryAxis> = Object.fromEntries(
  (Object.entries(BRAND_QUERY_INTENTS) as [BrandQueryAxis, readonly BrandQueryIntent[]][])
    .flatMap(([axis, intents]) => intents.map((i) => [i, axis])),
) as Record<BrandQueryIntent, BrandQueryAxis>;

/**
 * THE one place the `'legacy'` dispatch mode split lives (C1 / 00b DQ15).
 * `compare` reaches `comparison` only once the tenant has left `'legacy'`;
 * a `'legacy'` tenant's compare prompt stays on `narrative` (today's rubric).
 */
export function axisForIntent(intent: BrandQueryIntent, mode: PromptDispatchMode): BrandQueryAxis {
  if (mode === 'legacy' && intent === 'compare') return 'narrative';
  return AXIS_BY_INTENT[intent];
}

/**
 * The compléments circonstanciels kinds (C5).
 * Stored: only the kinds whose noul is true (what the TEXT says).
 * Effective (for level/display, never stored): stored ∪ {'lieu'} when the
 * effective zone has a type finer than `country`.
 */
export type ComplementKind = 'temps' | 'lieu' | 'maniere' | 'moyen' | 'cause' | 'consequence' | 'but';
export const COMPLEMENT_KINDS: readonly ComplementKind[] = [
  'temps', 'lieu', 'maniere', 'moyen', 'cause', 'consequence', 'but',
] as const;

/**
 * C5 — compute effective complements and level from stored `found` + zone.
 * `zoneMeta` is optional (lot C passes it; lot A only from `setFamilyZone`).
 * `effective` is for level/display only — NEVER stored.
 */
export function levelFromComplements(
  found: ComplementKind[],
  zoneMeta?: TargetZone | null,
): { effective: ComplementKind[]; level: VisibilityLevel } {
  const finerThanCountry =
    zoneMeta != null &&
    zoneMeta.type !== 'country' &&
    zoneMeta.type !== undefined;
  const effective = finerThanCountry && !found.includes('lieu')
    ? [...found, 'lieu'].filter((k, i, a) => a.indexOf(k) === i)
    : found;
  // Order by COMPLEMENT_KINDS
  const ordered = COMPLEMENT_KINDS.filter((k) => effective.includes(k));
  const level = (['L5', 'L4', 'L3', 'L2', 'L1'] as VisibilityLevel[])[Math.min(ordered.length, 4)];
  return { effective: ordered, level };
}

/**
 * C4 / decision #40 — THE prompt language list (34 ISO 639-1 codes).
 * Used by the classifier (call 1), pickers (B, D, I) and the per-run projection.
 * FR name = picker label (sorted by `name` with `localeCompare(…, 'fr')`).
 * NOT all measurable until lot C ships.
 */
export const PROMPT_LANGUAGES: readonly { code: string; name: string }[] = [
  { code: 'fr', name: 'français' },    { code: 'en', name: 'anglais' },
  { code: 'de', name: 'allemand' },    { code: 'es', name: 'espagnol' },
  { code: 'it', name: 'italien' },     { code: 'pt', name: 'portugais' },
  { code: 'nl', name: 'néerlandais' }, { code: 'pl', name: 'polonais' },
  { code: 'cs', name: 'tchèque' },     { code: 'sk', name: 'slovaque' },
  { code: 'hu', name: 'hongrois' },    { code: 'ro', name: 'roumain' },
  { code: 'bg', name: 'bulgare' },     { code: 'el', name: 'grec' },
  { code: 'hr', name: 'croate' },      { code: 'sl', name: 'slovène' },
  { code: 'sr', name: 'serbe' },       { code: 'da', name: 'danois' },
  { code: 'sv', name: 'suédois' },     { code: 'no', name: 'norvégien' },
  { code: 'fi', name: 'finnois' },     { code: 'et', name: 'estonien' },
  { code: 'lv', name: 'letton' },      { code: 'lt', name: 'lituanien' },
  { code: 'ga', name: 'irlandais' },   { code: 'mt', name: 'maltais' },
  { code: 'lb', name: 'luxembourgeois' }, { code: 'ca', name: 'catalan' },
  { code: 'eu', name: 'basque' },      { code: 'gl', name: 'galicien' },
  { code: 'uk', name: 'ukrainien' },   { code: 'ru', name: 'russe' },
  { code: 'tr', name: 'turc' },        { code: 'ar', name: 'arabe' },
] as const;

export const PROMPT_LANGUAGE_CODES: readonly string[] = PROMPT_LANGUAGES.map((l) => l.code);

/** Classifier version, starts at 1 (C1). */
export const PROMPT_CLASSIFIER_VERSION = 1;

/** Max items per saving bulk request (C3). */
export const BULK_MAX_ITEMS = 100;

/** Max items for a dryRun with `items` (C3). */
export const BULK_DRYRUN_MAX_ITEMS = 1000;

/** U1 per-axis cap kept for tenants still on `'legacy'` dispatch (C3 / 00b DQ14). */
export const LEGACY_AXIS_PROMPT_CAP = 1000;

/**
 * Where a query came from.
 *   - geo_strategy_inferred : generated by QueryGeneratorService. Freely replaceable.
 *   - user_added            : user typed it. Regeneration never touches it.
 *   - user_edited           : started as inferred, user modified. Regeneration never touches it.
 *   - csv_import / api / search_console / keywords : other creation sources (C3 / H).
 *   - variant_language / variant_zone : created as a variant of a root prompt (D / E).
 */
export type BrandQuerySource =
  | 'geo_strategy_inferred' | 'user_added' | 'user_edited'
  | 'csv_import' | 'api' | 'search_console' | 'keywords'
  | 'variant_language' | 'variant_zone';

export type BrandQueryStatus = 'active' | 'archived';

/**
 * Assignment of a referral query to the L1–L5 visibility ladder.
 *   - pending  : classification in flight (async after create/edit)
 *   - <Lx>     : classifier-assigned level (or generator-assigned on initial creation)
 *   - null     : field does not apply (narrative / authority)
 */
export type BrandQueryLevel = VisibilityLevel | 'pending' | null;

/**
 * First-class, user-editable query row. One DynamoDB item per row.
 *
 * Key shape:
 *   PK = `BRAND#<brandId>`
 *   SK = `QUERY#<axis>#<queryId>`
 *   GSI1: tenantId → `BRAND#<brandId>#<axis>` for tenant-scoped listing
 *
 * Cadence: a `textHash` invariant lets us detect reworded queries in per-query
 * trend sparklines — every audit result row snapshots the `textHash` at run time
 * as `queryTextHash`, so when the hash changes we render a distinct marker instead
 * of treating the reworded query as the same series.
 */
export interface BrandQuery {
  tenantId: string;
  brandId: string;
  queryId: string;
  /** CHANGED: `'pending'` until classified (C2). */
  axis: BrandQueryAxis | 'pending';
  text: string;
  /** sha1 of normalized text (lowercase, whitespace-collapsed, punctuation-stripped). */
  textHash: string;
  /** L1..L5 for referral, or 'pending' while the classifier is in flight. null elsewhere. */
  level?: BrandQueryLevel;
  /** Referral only — geographic zone slug (legacy; zoneMeta is the new store). */
  zone?: string;
  /** Zone metadata (C1, lot C). Absent = global (Mondial) or unresolved legacy zone. */
  zoneMeta?: TargetZone;
  /** Family id — equals the root prompt's queryId on a variant; absent on root/standalone (C1 lot D/E). */
  familyId?: string;
  /**
   * Target language for this prompt — independent of where the user lives.
   * Values are ISO 639-1 codes from PROMPT_LANGUAGE_CODES.
   */
  language?: string;
  /**
   * Classified intent (C4). Absent = not classified or classification failed.
   */
  intent?: BrandQueryIntent;
  /** Classification confidence 0..1, advisory only (C4). */
  intentConfidence?: number;
  /** ISO date of last classification (C4). */
  classifiedAt?: string;
  /** Classifier version, starts at 1 (C4). */
  classifierVersion?: number;
  /**
   * Source group: authority topic (axis=authority) or use-case (axis=referral).
   * From the brand's list only (C4).
   */
  topic?: string;
  /** Image and Comparaison: subset of strategy.decisionCriteria (C4). */
  criteria?: string[];
  /** Image and Comparaison: a Cibles target id (C4, lot F). */
  targetId?: string;
  /** Image and Comparaison: target category (C4, lot F). */
  targetCategory?: TargetCategory;
  /**
   * Referral only: compléments found IN THE TEXT (C5). `lieu` from zoneMeta
   * is NEVER stored here — it is added at level computation only.
   * `[]` = success, none found (L5 referral). Absent = not classified.
   */
  complements?: ComplementKind[];
  /**
   * Reason this row is archived (C1 R2). Only on `status: 'archived'`.
   *   absent       = today's archive (reactivatable alone)
   *   'user'       = « Désactiver »
   *   'plan_limit' = C17.6 reconcile (lot G)
   *   'deleted'    = « Supprimer » or a removed language/place variant
   */
  archivedReason?: 'user' | 'plan_limit' | 'deleted';
  source: BrandQuerySource;
  /**
   * Free-text segmentation labels for the BP dashboard. A SEPARATE namespace
   * from Cibles tags (which are uint16 ids in the targets manifest) — these are
   * plain names stored on this row, no registry object, no migration.
   *
   * Normalized on write: trimmed, empties dropped, casefold-deduped (the first
   * occurrence's casing wins), order preserved. Archived prompts keep their
   * tags so dashboard history keeps resolving them.
   */
  tags?: string[];
  /** Hash of the Brand Strategy + GEO Strategy snapshot when the row was last generated. */
  geoStrategyHash?: string;
  status: BrandQueryStatus;
  createdAt: string;
  updatedAt: string;
}

/** FOUR axes in C9 display order (Comparaison last). */
export const BRAND_QUERY_AXES: readonly BrandQueryAxis[] = ['narrative', 'authority', 'referral', 'comparison'] as const;

export const BRAND_QUERY_LEVELS: readonly VisibilityLevel[] = ['L1', 'L2', 'L3', 'L4', 'L5'] as const;

export const BRAND_QUERY_TEXT_MAX_CHARS = 300;

/** Prompt tags per prompt. Over-cap is rejected (400), never truncated. */
export const BRAND_QUERY_MAX_TAGS = 10;

/** Max characters of a single prompt tag name. Over-length is rejected (400). */
export const BRAND_QUERY_TAG_MAX_CHARS = 40;

/**
 * DISTINCT prompt tags per brand — LOAD-BEARING, not cosmetic.
 *
 * The BP dashboard pre-folds per-tag numbers at WRITE time into a `byTag` MAP
 * inside a single DynamoDB trend row (docs/plans/bp-dashboard.md §2.3), so the
 * number of distinct tags on a brand is the number of keys in that map. With
 * free text and no cap, 1 000 prompts × 10 tags = up to 10 000 keys, which
 * blows the 400 KB item limit and makes every dashboard write fail. Enforced
 * when a tag is INTRODUCED (create / patch / import) by counting distinct tags
 * across the brand's prompt rows and rejecting the 101st.
 */
export const BRAND_TAG_REGISTRY_MAX = 100;

// ---------------------------------------------------------------------------
// Bulk endpoint types (C3)
// ---------------------------------------------------------------------------

/** One classified item returned from the bulk dryRun (C3 / C4). */
export interface BulkClassified {
  text: string;
  intent?: BrandQueryIntent;        // absent = could not classify → « en attente »
  axis?: BrandQueryAxis;
  language?: string;
  topic?: string;
  criteria?: string[];
  targetId?: string;
  targetCategory?: TargetCategory;
  targetLookup?: 'failed';          // Cibles search errored: target unknown
  complements?: ComplementKind[];
  level?: VisibilityLevel;          // referral only
  confidence?: number;
}

/**
 * One item in the reviewed commit body (C3 path 2).
 * `intent` absent → saved as pending, not classified in this request.
 */
export interface BulkItem {
  text: string;
  intent?: BrandQueryIntent;
  language?: string;
  complements?: ComplementKind[];
  topic?: string | null;
  criteria?: string[];
  targetId?: string | null;
  zoneMeta?: TargetZone | null;
  tags?: string[];
}

// ---------------------------------------------------------------------------
// Language-variant types (lot D)
// ---------------------------------------------------------------------------

/** Max roots × languages per translate / save request (lot D). */
export const VARIANT_TRANSLATE_MAX = 100;
/** Max roots × items in one preview: true call (lot D). */
export const VARIANT_PREVIEW_MAX = 1000;

/** Lot D §1: a flag on a translation proposal. */
export type ProperNounFlag =
  | { kind: 'missing'; entity: string }
  | { kind: 'added'; entity: string }
  | { kind: 'identical' }
  | { kind: 'place_added'; place: string }
  | { kind: 'failed' };

/** Lot D §3: one translation proposal for a root × language. */
export interface TranslationProposal {
  rootId: string;
  language: string;
  text?: string;
  entitiesChecked: string[];
  survived: string[];
  flags: ProperNounFlag[];
  approvable: boolean;
  defaultChecked: boolean;
  token?: string;
}

export interface TranslateVariantsRequest {
  roots: { queryId: string; axis: BrandQueryAxis }[];
  languages: string[];
}
export interface TranslateVariantsResponse {
  proposals: TranslationProposal[];
}

export interface CreateLanguageVariantsRequest {
  roots: { queryId: string; axis: BrandQueryAxis; items: { language: string; text: string; token: string }[] }[];
  preview?: boolean;
}
export interface CreateLanguageVariantsResponse {
  created: BrandQuery[];
  errors: { rootId: string; index: number; language: string; placeKey?: string; error: string }[];
  figuresAfter?: AllowanceFigures;
  refused?: boolean;
  warnings?: SaveWarnings[];
  firstRun?: FirstRunNotice[];
}

/** Lot E: POST .../queries/:queryId/zone-variants — add places to the opened row's family. */
export const ZONE_VARIANTS_MAX = 20;
export interface CreateZoneVariantsRequest {
  axis: BrandQueryAxis;      // the opened row's CURRENT key (C2); every created row is written on it
  zones: TargetZone[];       // 1..ZONE_VARIANTS_MAX
  preview?: boolean;         // true = every check, rows built, NOTHING written or queued
}
export interface CreateZoneVariantsResponse {
  created: BrandQuery[];     // [] with preview
  /** index = zone index; language = the failed row's defaulted language. */
  errors: { index: number; zone: string; language?: string; error: string }[];
  figuresAfter?: AllowanceFigures;
  refused?: boolean;
  warnings?: SaveWarnings[];
  firstRun?: FirstRunNotice[];
}

/** Request body for POST .../queries/bulk (C3). */
export interface BulkRequest {
  texts?: string[];                              // path 1: raw texts
  items?: BulkItem[];                            // path 2: reviewed commit
  dryRun?: boolean;
  source: BrandQuerySource;
  defaults?: { zoneMeta?: TargetZone | null; tags?: string[] };
}

/** Response for POST .../queries/bulk (C3). */
export type BulkResponse =
  | {
      dryRun: true;
      items: BulkClassified[];
      figuresAfter?: AllowanceFigures;
      refused?: boolean;
      warnings?: SaveWarnings[];
      firstRun?: FirstRunNotice[];
    }
  | {
      dryRun: false;
      created: BrandQuery[];
      errors: { index: number; text: string; error: string }[];
      figuresAfter?: AllowanceFigures;
      refused?: boolean;
      warnings?: SaveWarnings[];
      firstRun?: FirstRunNotice[];
    };
